import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Terminal, Cpu, Layers, Box, MousePointer2, Activity } from 'lucide-react';

export const TracePreview: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  const steps = [
    { pc: '000', op: 'PUSH1', val: '0x80', stack: ['0x80'] },
    { pc: '002', op: 'PUSH1', val: '0x40', stack: ['0x40', '0x80'] },
    { pc: '004', op: 'MSTORE', val: '', stack: [] },
    { pc: '005', op: 'CALLVALUE', val: '', stack: ['0xde0b6b3a7640000'] },
    { pc: '006', op: 'DUP1', val: '', stack: ['0xde0b6b3a7640000', '0xde0b6b3a7640000'] },
    { pc: '007', op: 'ISZERO', val: '', stack: ['0x0', '0xde0b6b3a7640000'] },
    { pc: '008', op: 'PUSH2', val: '0x0010', stack: ['0x0010', '0x0', '0xde0b6b3a7640000'] },
    { pc: '011', op: 'JUMPI', val: '', stack: ['0xde0b6b3a7640000'] },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % steps.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative w-full max-w-5xl mx-auto mt-20 group">
      {/* Decorative elements */}
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-emerald-500/10 blur-3xl rounded-full" />
      <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-blue-500/10 blur-3xl rounded-full" />
      
      {/* Main Frame */}
      <motion.div 
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative bg-[#0A0A0A] border border-[#262626] rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
      >
        {/* Browser-like Header */}
        <div className="h-10 bg-[#141414] border-b border-[#262626] flex items-center px-4 gap-2">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-[#262626]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#262626]" />
            <div className="w-2.5 h-2.5 rounded-full bg-[#262626]" />
          </div>
          <div className="mx-auto bg-[#0A0A0A] border border-[#262626] rounded px-3 py-0.5 text-[9px] font-mono text-[#525252] flex items-center gap-2">
            <span className="text-emerald-500">https://</span>
            oru.trace/debug/0x4497...8f0
          </div>
          <div className="flex items-center gap-2">
            <Activity className="w-3 h-3 text-emerald-500 animate-pulse" />
            <span className="text-[8px] font-mono text-emerald-500/50 uppercase tracking-tighter">Live Sync</span>
          </div>
        </div>

        {/* Mock UI Content */}
        <div className="flex flex-col md:flex-row md:h-[500px] h-auto overflow-hidden">
          {/* Left Sidebar: Call Tree */}
          <div className="w-full md:w-1/3 border-b md:border-b-0 md:border-r border-[#262626] p-4 flex flex-col gap-4 h-[180px] md:h-full overflow-y-auto">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#525252]">
              <Layers className="w-3 h-3" />
              Call Tree
            </div>
            <div className="space-y-2">
              {[
                { type: 'CALL', to: '0x7a25...488d', val: '0.5 ETH', active: true },
                { type: 'STATICCALL', to: '0xc02a...6cc2', val: '0 ETH', indent: true },
                { type: 'DELEGATECALL', to: '0x1111...1111', val: '0 ETH', indent: true },
                { type: 'CALL', to: '0xdef1...7890', val: '0.1 ETH' },
              ].map((item, i) => (
                <motion.div 
                  key={i} 
                  animate={item.active ? { backgroundColor: ['rgba(16,185,129,0.05)', 'rgba(16,185,129,0.1)', 'rgba(16,185,129,0.05)'] } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                  className={`p-2 rounded border ${item.active ? 'border-emerald-500/20' : 'border-transparent'} flex flex-col gap-1`}
                  style={{ marginLeft: item.indent ? '12px' : '0' }}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-[8px] font-bold ${item.active ? 'text-emerald-500' : 'text-[#404040]'}`}>{item.type}</span>
                    <span className="text-[8px] text-[#525252] font-mono">{item.val}</span>
                  </div>
                  <span className={`text-[9px] font-mono ${item.active ? 'text-white' : 'text-[#A3A3A3]'}`}>{item.to}</span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Center: Opcode Trace */}
          <div className="flex-1 flex flex-col border-b md:border-b-0 border-[#262626] h-[300px] md:h-full overflow-hidden">
            <div className="h-8 border-b border-[#262626] bg-[#0F0F0F] flex items-center px-4 justify-between">
              <div className="flex items-center gap-2 text-[9px] font-bold uppercase tracking-widest text-[#525252]">
                <Terminal className="w-3 h-3 text-emerald-500" />
                Opcode Trace
              </div>
              <div className="text-[9px] font-mono text-[#404040]">STEP {activeStep + 1} / {steps.length}</div>
            </div>
            <div className="flex-1 p-4 font-mono text-[10px] space-y-1 overflow-y-auto relative">
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-[#0A0A0A]/50" />
              {steps.map((step, i) => (
                <motion.div 
                  key={i} 
                  animate={i === activeStep ? { x: [0, 4, 0] } : {}}
                  className={`flex items-center gap-4 py-0.5 px-2 rounded transition-colors duration-300 ${i === activeStep ? 'bg-emerald-500/10 text-white' : 'text-[#525252]'}`}
                >
                  <span className="w-8 text-right opacity-30">{step.pc}</span>
                  <span className={i === activeStep ? 'text-emerald-400 font-bold' : ''}>{step.op}</span>
                  <span className="text-[#404040]">{step.val}</span>
                </motion.div>
              ))}
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#262626] mt-4">
                <div>
                  <div className="text-[9px] font-bold text-[#525252] uppercase mb-2">Stack</div>
                  <div className="space-y-1">
                    <AnimatePresence mode="popLayout">
                      {steps[activeStep].stack.map((val, i) => (
                        <motion.div 
                          key={`${activeStep}-${i}-${val}`}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: 10 }}
                          className="flex gap-2 text-blue-400 font-mono text-[9px]"
                        >
                          <span className="text-[#404040]">[{i}]</span> {val}
                        </motion.div>
                      ))}
                      {steps[activeStep].stack.length === 0 && (
                        <div className="text-[#404040] italic text-[9px]">EMPTY</div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>
                <div>
                  <div className="text-[9px] font-bold text-[#525252] uppercase mb-2">Memory</div>
                  <div className="font-mono text-[8px] text-[#404040] leading-tight">
                    00: 00000000 00000000<br />
                    10: {activeStep > 2 ? '00000000 00000040' : '00000000 00000000'}<br />
                    20: {activeStep > 2 ? '00000000 00000080' : '00000000 00000000'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar: Storage */}
          <div className="w-full md:w-1/4 md:border-l border-[#262626] bg-[#0F0F0F] p-4 flex flex-col gap-4 h-[180px] md:h-full overflow-y-auto">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-[#525252]">
              <Box className="w-3 h-3" />
              Storage
            </div>
            <div className="space-y-3">
              {[
                { slot: '0x0', val: activeStep > 4 ? '0x00...0002' : '0x00...0001', label: 'Owner', changed: activeStep === 5 },
                { slot: '0x1', val: activeStep > 2 ? '0x00...03e9' : '0x00...03e8', label: 'Balance', changed: activeStep === 3 },
              ].map((s, i) => (
                <motion.div 
                  key={i} 
                  animate={s.changed ? { borderColor: ['rgba(38,38,38,1)', 'rgba(16,185,129,0.5)', 'rgba(38,38,38,1)'] } : {}}
                  className="bg-[#1A1A1A] p-2 rounded border border-[#262626] transition-all"
                >
                  <div className="text-[8px] text-emerald-500 font-bold mb-1 uppercase tracking-tighter">{s.label}</div>
                  <div className="text-[8px] text-[#525252] font-mono truncate">{s.slot}</div>
                  <motion.div 
                    key={s.val}
                    initial={s.changed ? { color: '#10b981' } : {}}
                    animate={{ color: '#ffffff' }}
                    className="text-[9px] font-mono truncate"
                  >
                    {s.val}
                  </motion.div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Status Bar */}
        <div className="h-6 bg-[#0A0A0A] border-t border-[#262626] flex items-center px-4 justify-between text-[8px] font-mono text-[#404040]">
          <div className="flex items-center gap-3">
            <span className="text-emerald-500/50">● ENGINE ONLINE</span>
            <span>NETWORK: MAINNET</span>
          </div>
          <motion.div
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 3, repeat: Infinity }}
          >
            BLOCK: 19442031
          </motion.div>
        </div>
      </motion.div>

      {/* Floating Badge */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.5 }}
        className="absolute -right-6 top-1/2 -translate-y-1/2 bg-emerald-500 text-black px-4 py-2 rounded-lg font-bold text-[10px] uppercase tracking-[0.2em] shadow-xl rotate-90 origin-right hidden md:block"
      >
        Live Trace View
      </motion.div>

      {/* Animated Cursor */}
      <motion.div
        animate={{
          x: [100, 300, 200, 400],
          y: [100, 200, 150, 300],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: "easeInOut"
        }}
        className="absolute z-20 pointer-events-none hidden md:block"
      >
        <MousePointer2 className="w-5 h-5 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]" />
        <div className="ml-4 mt-2 bg-emerald-500 text-black text-[8px] font-bold px-1.5 py-0.5 rounded uppercase tracking-tighter">
          Tracing...
        </div>
      </motion.div>
    </div>
  );
};
